/**
 * Lightweight email typo checker
 * Replaces the mailcheck library with a focused, modern implementation
 */

// Modern popular email domains (updated for 2024)
const POPULAR_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'protonmail.com',
  'me.com',
  'mac.com',
  'live.com',
  'msn.com',
  'ymail.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'cox.net'
];

/**
 * Calculate Levenshtein distance between two strings
 * This measures the minimum number of single-character edits needed to change one string into another
 */
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  
  // Create a 2D array for dynamic programming
  const dp: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));
  
  // Initialize first row and column
  for (let i = 0; i <= len1; i++) {
    dp[i][0] = i;
  }
  for (let j = 0; j <= len2; j++) {
    dp[0][j] = j;
  }
  
  // Fill the dp table
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,    // deletion
          dp[i][j - 1] + 1,    // insertion
          dp[i - 1][j - 1] + 1 // substitution
        );
      }
    }
  }
  
  return dp[len1][len2];
}

/**
 * Calculate similarity percentage between two strings
 */
function getSimilarityPercentage(str1: string, str2: string): number {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  
  if (maxLength === 0) {return 100;}
  
  return ((maxLength - distance) / maxLength) * 100;
}

/**
 * Find the closest matching domain from popular domains
 */
function findClosestDomain(inputDomain: string, threshold = 70): string | null {
  let bestMatch: string | null = null;
  let bestSimilarity = 0;
  
  for (const popularDomain of POPULAR_DOMAINS) {
    const similarity = getSimilarityPercentage(inputDomain, popularDomain);
    
    if (similarity > bestSimilarity && similarity >= threshold) {
      bestSimilarity = similarity;
      bestMatch = popularDomain;
    }
  }
  
  // Don't suggest if it's already correct
  if (bestMatch === inputDomain) {
    return null;
  }
  
  return bestMatch;
}

/**
 * Check for email typos and suggest corrections
 * @param email The email address to check
 * @returns Suggested email if a typo is detected, empty string otherwise
 */
export function checkEmailTypo(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }
  
  const emailLower = email.toLowerCase().trim();
  const atIndex = emailLower.lastIndexOf('@');
  
  // Invalid email format
  if (atIndex === -1 || atIndex === 0 || atIndex === emailLower.length - 1) {
    return '';
  }
  
  const localPart = emailLower.substring(0, atIndex);
  const domain = emailLower.substring(atIndex + 1);
  
  // Skip if domain is empty or too short
  if (domain.length < 3) {
    return '';
  }
  
  // Check if domain is already in our popular list (exact match)
  if (POPULAR_DOMAINS.includes(domain)) {
    return '';
  }
  
  // Find closest domain with at least 70% similarity
  const suggestedDomain = findClosestDomain(domain);
  
  if (suggestedDomain) {
    return `${localPart}@${suggestedDomain}`;
  }
  
  return '';
}

/**
 * Add custom domains to the checker (useful for corporate domains)
 */
export function addCustomDomains(domains: string[]): void {
  for (const domain of domains) {
    if (domain && !POPULAR_DOMAINS.includes(domain.toLowerCase())) {
      POPULAR_DOMAINS.push(domain.toLowerCase());
    }
  }
}

/**
 * Get the list of domains being checked against
 */
export function getCheckedDomains(): string[] {
  return [...POPULAR_DOMAINS];
}